import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import './App.css';
import Home from './pages/Home';
import ServiceDetail from './pages/ServiceDetail';
import TeamContact from './pages/TeamContact';
import WhyUs from './pages/WhyUs';
import Insights from './pages/Insights';
import NotFound from './pages/NotFound';
import ScrollToTop from "./ScrollToTop";

function DynamicTitle() {
  const location = useLocation();
  
  React.useEffect(() => {
    let title = "BY HW CONSULTING";
    const path = location.pathname;
    
    if (path === '/') title = "Home | BY HW CONSULTING";
    else if (path === '/why-us') title = "About Us | BY HW CONSULTING";
    else if (path === '/insights') title = "Insights | BY HW CONSULTING";
    else if (path === '/contact') title = "Contact | BY HW CONSULTING";
    else if (path.startsWith('/services/')) {
      // Best effort title, could be made more specific by passing actual service name
      title = "Services | BY HW CONSULTING";
    }
    
    document.title = title;
  }, [location]);
  
  return null;
}

function Layout({ children }) {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <nav className={`byhw-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <Link to="/" className="byhw-logo" style={{textDecoration:'none', display:'flex', alignItems:'center', height:'100%'}}>
          <img src="/byhw.webp" alt="by HW Consulting" style={{height:'44px', width:'auto', display:'block'}} />
        </Link>
        
        {/* Desktop Nav */}
        <div className="byhw-nav-links desktop-only">
          <div className="mega-menu-wrapper">
            <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
              Services <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:4, marginTop:2}}><polyline points="6 9 12 15 18 9"></polyline></svg>
            </Link>
            <div className="mega-menu-panel">
              <div className="mega-menu-inner">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                  <div className="mega-col">
                    <h4>Core Capabilities</h4>
                    <Link to="/service/tax">Tax Preparation & Planning</Link>
                    <Link to="/service/accounting">Accounting & Bookkeeping</Link>
                    <Link to="/service/payroll">Payroll Services</Link>
                    <Link to="/service/audits">Audit, Review & Compilation</Link>
                    <Link to="/service/financial">CFO & Financial Advisory</Link>
                    <Link to="/service/consulting">Business Consulting</Link>
                  </div>
                  <div className="mega-col">
                    <h4>Specialized Advisory</h4>
                    <Link to="/service/formation">Entity Formation</Link>
                    <Link to="/service/forensic">Forensic Accounting</Link>
                    <Link to="/service/salt">State & Local Tax (SALT)</Link>
                    <Link to="/service/wealth">Wealth & Estate Planning</Link>
                    <Link to="/service/rnd">R&D Tax Credits</Link>
                    <Link to="/service/risk">SOC & Risk Advisory</Link>
                  </div>
                </div>
                <div className="mega-feature">
                  <div>
                    <h4>The HW Advantage</h4>
                    <p>Decades of institutional expertise delivering proactive tax strategies and precision accounting for your enterprise.</p>
                    <Link to="/why-us" className="mega-feature-link">Learn About Our Firm &rarr;</Link>
                  </div>
                  <div>
                    <h4>Thought Leadership</h4>
                    <p>Proprietary research and actionable frameworks.</p>
                    <Link to="/insights" className="mega-feature-link">Explore Insights &rarr;</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Link to="/why-us" className={location.pathname === '/why-us' ? 'active' : ''}>About</Link>
          <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>Contact</Link>
          <Link to="/contact" className="nav-cta-btn">Free Consultation</Link>
        </div>

        {/* Mobile Hamburger Toggle */}
        <button 
          className={`hamburger-toggle ${mobileMenuOpen ? 'open' : ''} mobile-only`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label="Toggle navigation menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      <div id="mobile-menu" className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} aria-hidden={!mobileMenuOpen}>
        <div className="mobile-menu-content">
          <div className="mobile-accordion">
            <button 
              className="mobile-accordion-btn"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            >
              Services
              <svg 
                aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                style={{transform: mobileServicesOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s'}}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div className={`mobile-accordion-content ${mobileServicesOpen ? 'open' : ''}`}>
              <div className="mobile-accordion-inner">
                <div className="mobile-accordion-label">Core Capabilities</div>
                <Link to="/service/tax">Tax Preparation & Planning</Link>
                <Link to="/service/accounting">Accounting & Bookkeeping</Link>
                <Link to="/service/payroll">Payroll Services</Link>
                <Link to="/service/audits">Audit, Review & Compilation</Link>
                <Link to="/service/financial">CFO & Financial Advisory</Link>
                <Link to="/service/consulting">Business Consulting</Link>
                
                <div className="mobile-accordion-label" style={{marginTop:24}}>Specialized Advisory</div>
                <Link to="/service/formation">Entity Formation</Link>
                <Link to="/service/forensic">Forensic Accounting</Link>
                <Link to="/service/salt">State & Local Tax (SALT)</Link>
                <Link to="/service/wealth">Wealth & Estate Planning</Link>
                <Link to="/service/rnd">R&D Tax Credits</Link>
                <Link to="/service/risk">SOC & Risk Advisory</Link>
              </div>
            </div>
          </div>
          <Link to="/why-us" className={location.pathname === '/why-us' ? 'active' : ''}>About</Link>
          <Link to="/insights" className={location.pathname === '/insights' ? 'active' : ''}>Insights</Link>
          <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>Contact</Link>
          <Link to="/contact" className="mobile-cta-btn" style={{marginTop: 16}} onClick={() => setMobileMenuOpen(false)}>Engage Our Firm</Link>
        </div>
      </div>

      <main>{children}</main>
      <footer className="byhw-footer">
        <div className="byhw-footer-inner">
          <div className="byhw-footer-brand">
            <img src="/byhw.webp" alt="by HW Consulting" style={{height:'32px', filter:'brightness(0) invert(1)'}} />
          </div>
          <div className="byhw-footer-col">
            <h4>Navigation</h4>
            <Link to="/">Our Expertise</Link>
            <Link to="/why-us">About The Firm</Link>
            <Link to="/insights">Insights & Whitepapers</Link>
          </div>
          <div className="byhw-footer-col">
            <h4>Contact</h4>
            <a href="mailto:Contact@byhwconsulting.com">Contact@byhwconsulting.com</a>
            <Link to="/contact">Schedule Consultation</Link>
          </div>
        </div>
        <div className="byhw-footer-bottom">
          <span>&copy; {new Date().getFullYear()} by HW Consulting. All rights reserved.</span>
          <span style={{maxWidth:'600px', textAlign:'right', lineHeight: 1.5}}>
            by HW Consulting is an independent professional services firm. All engagements are subject to applicable professional standards.
          </span>
        </div>
      </footer>
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/service/:id" element={<ServiceDetail />} />
        <Route path="/team" element={<TeamContact />} />
        <Route path="/contact" element={<TeamContact />} />
        <Route path="/why-us" element={<WhyUs />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <DynamicTitle />
      <div className="byhw-root">
        <Layout>
          <AnimatedRoutes />
        </Layout>
      </div>
    </Router>
  );
}

export default App;
